import { FC, useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import ButtonBase from 'src/components/Button';
import ModalLayoutBase, { MobileModalType, ModalContent, ModalFooter as ModalFooterBase } from 'src/components/ModalLayout';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { setFeatureSettingsAtom } from 'src/stores/user/atom';
import { UserSettingType } from 'src/stores/user/types';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
import AssistantConsentLeaveModal from './AssistantConsentLeaveModal';
export type AssistantConsentEntryPoint = 'signup' | 'mirror' | 'settings';
interface AssistantConsentModalProps {
  entryPoint: AssistantConsentEntryPoint;
  onConsented?: () => void;
  onSkipped?: () => void;
}
const ModalLayout = styled(ModalLayoutBase)`
  width: 500px;
`;
const Header = styled.div`
  z-index: 1;
  padding: 20px 24px 8px;
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  flex-shrink: 0;
`;
const ModalTitle = styled.p`
  flex: 1;
  font-size: 30px;
  font-weight: 700;
  line-height: 40px;
  color: ${({
  theme
}) => theme.color.content12};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({
  theme
}) => theme.screenSize.mobile} {
    font-size: 20px;
    line-height: 26px;
  }
`;
const SkipButton = styled.button`
  all: unset;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.content8};
  text-align: end;
  white-space: nowrap;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -7px -8px;
    min-width: 40px;
    min-height: 40px;
  }
`;
const ContentBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
  flex: 1;
  justify-content: center;
`;
const GraphicWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 0;
`;
const GraphicImage = styled.img`
  width: 200px;
  height: 200px;
  object-fit: contain;

  ${({
  theme
}) => theme.screenSize.mobile} {
    width: 164px;
    height: 164px;
  }
`;
const ConsentBox = styled.div`
  border-radius: 16px;
  background: ${({
  theme
}) => theme.color.surface3};
  border: 1px solid ${({
  theme
}) => theme.color.surface4};
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: clip;
`;
const ConsentBoxTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: ${({
  theme
}) => theme.color.content11};
`;
const ConsentBoxDesc = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 17px;
  color: ${({
  theme
}) => theme.color.content9};

  a {
    color: ${({
  theme
}) => theme.color.content9};
    text-decoration: underline;
    font-weight: 700;
    :visited {
      color: ${({
  theme
}) => theme.color.content9};
    }
  }
`;
const ModalFooter = styled(ModalFooterBase)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Button = styled(ButtonBase)`
  width: 100%;
`;
export function toReferrer(entryPoint: AssistantConsentEntryPoint): string {
  return entryPoint === 'signup' ? 'onboarding' : entryPoint;
}

/**
 * 어시스턴트 동의 모달
 */
const AssistantConsentModal: FC<AssistantConsentModalProps> = ({
  entryPoint,
  onConsented,
  onSkipped
}) => {
  const {
    t
  } = useTranslation();
  const closeModal = useSetAtom(closeModalAtom);
  const showModal = useSetAtom(showModalAtom);
  const setFeatureSettings = useSetAtom(setFeatureSettingsAtom);
  const consentBoxRef = useRef<HTMLDivElement>(null);
  const referrer = toReferrer(entryPoint);
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.ANYWHERE__IMPRESSION_ASSISTANT_CONSENT_POPUP,
      eventParams: {
        action_category: 'impression',
        tab: 'anywhere',
        page: 'anywhere',
        target: 'assistant_consent_popup',
        referrer
      }
    });
  }, [referrer]);
  useEffect(() => {
    const el = consentBoxRef.current;
    if (!el) return;
    const handleLinkClick = (e: Event) => {
      if (!(e.target instanceof HTMLElement) || !e.target.closest('a')) return;
      pushEvent({
        eventType: EVENT_TYPE.PROMETHEUS,
        eventName: EVENT_NAME.ASSISTANT_CONSENT_POPUP__CLICK_LEARN_MORE,
        eventParams: {
          action_category: 'click',
          tab: 'anywhere',
          page: 'assistant_consent_popup',
          target: 'learn_more',
          referrer
        }
      });
    };
    el.addEventListener('click', handleLinkClick);
    return () => el.removeEventListener('click', handleLinkClick);
  }, [referrer]);
  const handleAgree = useCallback(async () => {
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.ASSISTANT_CONSENT_POPUP__CLICK_AGREE,
      eventParams: {
        action_category: 'click',
        tab: 'anywhere',
        page: 'assistant_consent_popup',
        target: 'agree',
        referrer
      }
    });
    await setFeatureSettings([{
      type: UserSettingType.ASSISTANT_CONSENTED,
      value: true
    }, {
      type: UserSettingType.ASSISTANT_ENABLED,
      value: true
    }]);
    closeModal(ModalType.ASSISTANT_CONSENT);
    onConsented?.();
  }, [setFeatureSettings, closeModal, onConsented, referrer]);
  const handleSkip = useCallback(() => {
    showModal({
      key: ModalType.ASSISTANT_CONSENT_LEAVE,
      component: () => <AssistantConsentLeaveModal onBack={() => {
        closeModal(ModalType.ASSISTANT_CONSENT_LEAVE);
      }} onLeave={() => {
        closeModal(ModalType.ASSISTANT_CONSENT_LEAVE);
        closeModal(ModalType.ASSISTANT_CONSENT);
        onSkipped?.();
      }} />
    });
  }, [showModal, closeModal, onSkipped]);
  return <ModalLayout isDim mobileType={MobileModalType.BOTTOM_SHEET} closeWhenClickOutside={false} closeWithESC={false} navButtonType={ModalNavButtonType.NONE} mobileNavButtonType={ModalNavButtonType.NONE} data-testid='AssistantConsentModal' data-sentry-element="ModalLayout" data-sentry-component="AssistantConsentModal" data-sentry-source-file="AssistantConsentModal.tsx">
      <Header data-sentry-element="Header" data-sentry-source-file="AssistantConsentModal.tsx">
        <ModalTitle data-sentry-element="ModalTitle" data-sentry-source-file="AssistantConsentModal.tsx">{t('azar_assistant.consent_popup_title')}</ModalTitle>
        <SkipButton onClick={handleSkip} data-testid='AssistantConsentModal-button-skip' data-sentry-element="SkipButton" data-sentry-source-file="AssistantConsentModal.tsx">
          {t('COMMON__SKIP')}
        </SkipButton>
      </Header>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="AssistantConsentModal.tsx">
        <ContentBody data-sentry-element="ContentBody" data-sentry-source-file="AssistantConsentModal.tsx">
          <GraphicWrapper data-sentry-element="GraphicWrapper" data-sentry-source-file="AssistantConsentModal.tsx">
            <GraphicImage src='/images/assistant/imgAssistantConsent.webp' alt='' data-sentry-element="GraphicImage" data-sentry-source-file="AssistantConsentModal.tsx" />
          </GraphicWrapper>
          <ConsentBox data-sentry-element="ConsentBox" data-sentry-source-file="AssistantConsentModal.tsx">
            <ConsentBoxTitle data-sentry-element="ConsentBoxTitle" data-sentry-source-file="AssistantConsentModal.tsx">{t('azar_assistant.consent_popup_tagline')}</ConsentBoxTitle>
            <ConsentBoxDesc ref={consentBoxRef} dangerouslySetInnerHTML={{
            __html: t('azar_assistant.consent_popup_desc')
          }} data-sentry-element="ConsentBoxDesc" data-sentry-source-file="AssistantConsentModal.tsx" />
          </ConsentBox>
        </ContentBody>
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="AssistantConsentModal.tsx">
        <Button id='assistant-consent-agree' onClick={handleAgree} data-testid='AssistantConsentModal-button-agree' data-sentry-element="Button" data-sentry-source-file="AssistantConsentModal.tsx">
          {t('azar_assistant.consent_popup_button_agree')}
        </Button>
      </ModalFooter>
    </ModalLayout>;
};
export default AssistantConsentModal;